import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import styles from './Pricing.module.scss'
import bkgImg from '@/assets/images/members/bkg.png'
import Heading, { EHeadingType } from '../Heading/Heading'
import SignUp from '../SignUp/SignUp'
import { Box, Modal } from '@mui/material'
import { AI_MONTHLY_PLAN, AI_YEARLY_PLAN, CAPTURE_MONTHLY_PLAN, CAPTURE_YEARLY_PLAN } from './Pricing.params'
import PricingCardPeriodicity from './PricingCardPeriodicity'
import PricingCardPrice from './PricingCardPrice'
import PricingToggle from './PricingToggle'
import { PlanId } from '@/hooks/useGoogleOAuth'

// Helper function to extract YouTube video ID from URL
const getYouTubeVideoId = (url: string): string => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : ''
}

export const PLANS_MONTHLY = [CAPTURE_MONTHLY_PLAN, AI_MONTHLY_PLAN]
export const PLANS_YEARLY = [CAPTURE_YEARLY_PLAN, AI_YEARLY_PLAN]

export default function Pricing() {
  const router = useRouter()
  const { code } = router.query

  useEffect(
    function getGoogleUserFromCode() {
      const getUser = async () => {
        if (!code) return
        const result = await fetch(`/api/get_google_user?code=${code}`)
        const json = await result.json()
        if (json.message !== 'success') return
        localStorage.setItem('user', JSON.stringify(json['user']))

        Sentry.setTag('email', json['user']?.email)
      }

      getUser()
    },
    [code],
  )

  const [isYearly, setIsYearly] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(1)
  const [openModal, setOpenModal] = useState(false)
  const [currentVideoId, setCurrentVideoId] = useState('')

  const selectedPlan = useMemo(() => {
    return isYearly ? PLANS_YEARLY[selectedIndex] : PLANS_MONTHLY[selectedIndex]
  }, [isYearly, selectedIndex])

  const handleOpenModal = (watchUrl: string) => {
    const videoId = getYouTubeVideoId(watchUrl)
    setCurrentVideoId(videoId)
    setOpenModal(true)
  }

  const handleCloseModal = () => setOpenModal(false)

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <Heading type={EHeadingType.h2} text="Lazy Pricing" subtext="Don't procrastinate to get Lazy."></Heading>

        <div
          className="container payment"
          style={{
            maxHeight: '720px',
            display: 'flex',
            justifyContent: 'space-around',
            width: '100%',
          }}
        >
          <div>
            <div
              style={{
                display: 'flex',
                alignSelf: 'center',
                justifySelf: 'center',
                alignItems: 'center',
                gap: '10px',
                color: 'var(--Text-Secondary, rgb(138, 140, 147))',
                textAlign: 'center',
                fontFeatureSettings: "'clig' off, 'liga' off",
                textShadow: '0px 0px 24px rgba(255, 255, 255, 0.10)',
                fontSize: '15px',
                fontStyle: 'normal',
                fontWeight: '300',
              }}
            >
              <PricingToggle isYearly={isYearly} onToggle={setIsYearly} />
              <p>Billed Yearly - save 25%</p>
            </div>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '18px',
                  margin: '40px 0',
                  marginTop: '0px',
                }}
                className={styles.plan_container}
              >
                <img className={styles.bkg} src={bkgImg.src} alt="" />
                {Object.values(isYearly ? PLANS_YEARLY : PLANS_MONTHLY).map((plan, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      marginTop: '30px',
                      backgroundColor: '#131416',
                      padding: '30px',
                      minWidth: '345px',
                      zIndex: 99,
                      cursor: 'pointer',
                      borderRadius: '8px',
                      border: selectedIndex === index ? '1px solid #fff' : '1px solid transparent',
                      '&:hover': {
                        border: '1px solid #fff',
                      },
                    }}
                    onClick={() => setSelectedIndex(index)}
                  >
                    <div style={{}}>
                      <PricingCardPeriodicity title={plan.title} subTitle={plan.subTitle} />
                      <PricingCardPrice price={plan.priceAmount} recurrence={'per month'} />
                    </div>
                    <div key={index}>
                      <p
                        style={{
                          color: 'rgba(138, 140, 147, 0.6)',
                          fontFamily: 'var(--font-inter)',
                          fontSize: '11px',
                          fontStyle: 'normal',
                          fontWeight: '500',
                          lineHeight: '16px',
                          letterSpacing: '0.44px',
                          textTransform: 'uppercase',
                          margin: '0 0 16px 0',
                        }}
                      >
                        {plan.includes}
                      </p>
                      <ul
                        style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                        }}
                      >
                        {plan.features.map((feature, index) => (
                          <li
                            key={index}
                            style={{
                              color: 'rgba(226, 228, 233, 0.7)',
                              fontFamily: 'var(--font-inter)',
                              fontSize: '14px',
                              fontStyle: 'normal',
                              fontWeight: '400',
                              lineHeight: '22px',
                              marginBottom: '6px',
                              paddingLeft: '20px',
                              position: 'relative',
                            }}
                          >
                            <span
                              style={{
                                position: 'absolute',
                                left: '0',
                                top: '0',
                                color: 'rgba(138, 140, 147, 0.6)',
                                fontSize: '14px',
                                lineHeight: '22px',
                              }}
                            >
                              —
                            </span>
                            {feature.text}{' '}
                            {feature.watchUrl && (
                              <span
                                style={{ fontFamily: 'system-ui', cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleOpenModal(feature.watchUrl)
                                }}
                              >
                                <span style={{ color: 'rgba(138, 140, 147, 0.6)' }}>[</span> <u>Watch</u> ↗︎
                                <span style={{ color: 'rgba(138, 140, 147, 0.6)' }}>]</span>
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Box>
                ))}
              </div>
              <div
                className={styles.signUpBtn}
                style={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}
              >
                <SignUp ctaText="Start your 14-day trial" selectedPlan={selectedPlan?.id as PlanId} />
              </div>
            </div>
          </div>
        </div>

        {/* YouTube Video Modal */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="video-modal"
          aria-describedby="pricing-feature-video"
        >
          <div className={styles.modal_content}>
            <div className={styles.video_wrapper}>
              <iframe
                className={styles.video_iframe}
                src={`https://www.youtube.com/embed/${currentVideoId}?autoplay=1`}
                title="Feature Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </Modal>
      </div>
    </section>
  )
}
